import { Component, Input, OnChanges } from '@angular/core';
import { Permission, DataScopeLevel } from '../../../services/authorization.service';

export interface ModuleTreeNode {
  id: string;
  label: string;
  icon: string;
  color: string;
}

export interface ModuleTreeGroup {
  key: string;
  label: string;
  icon: string;
  color: string;
  modules: ModuleTreeNode[];
}

@Component({
  selector: 'app-module-access-tree',
  templateUrl: './module-access-tree.component.html',
  styleUrls: ['./module-access-tree.component.scss'],
  standalone: false,
})
export class ModuleAccessTreeComponent implements OnChanges {
  // ── Structure ──
  @Input() groups: ModuleTreeGroup[] = [];
  @Input() isSuperAdmin = false;

  // ── Access state (per module) ──
  @Input() isGranted: (moduleId: string) => boolean = () => false;
  @Input() permission: (moduleId: string) => Permission = () => 'none';
  @Input() onToggleGrant: (moduleId: string) => void = () => {};
  @Input() onPermissionChange: (moduleId: string, permission: Permission) => void = () => {};

  // ── Scope state (per module) ──
  @Input() scopeLevel: (moduleId: string) => DataScopeLevel = () => 'global';
  @Input() scopeRegions: (moduleId: string) => string[] = () => [];
  @Input() scopeCountries: (moduleId: string) => string[] = () => [];
  @Input() scopeCompanies: (moduleId: string) => string[] = () => [];
  @Input() onScopeLevelChange: (moduleId: string, level: DataScopeLevel) => void = () => {};
  @Input() onToggleRegion: (moduleId: string, region: string) => void = () => {};
  @Input() onToggleCountry: (moduleId: string, country: string) => void = () => {};
  @Input() onToggleCompany: (moduleId: string, company: string) => void = () => {};

  // ── Master "All Modules" state ──
  @Input() allGranted = false;
  @Input() allPermission: Permission = 'read';
  @Input() onAllGrantedChange: (granted: boolean) => void = () => {};
  @Input() onAllPermissionChange: (permission: Permission) => void = () => {};

  // ── Options ──
  @Input() permissionOptions: Array<{ label: string; value: Permission }> = [];
  @Input() scopeLevels: Array<{ value: DataScopeLevel; label: string; icon: string }> = [];
  @Input() regions: string[] = [];
  @Input() countries: string[] = [];
  @Input() companies: string[] = [];

  // ── Expansion state ──
  expandedGroups = new Set<string>();
  expandedModules = new Set<string>();
  private seeded = false;

  ngOnChanges(): void {
    if (!this.seeded && this.groups.length) {
      this.groups.forEach(g => this.expandedGroups.add(g.key));
      this.seeded = true;
    }
  }

  // ── Group helpers ──
  toggleGroup(key: string): void {
    if (this.expandedGroups.has(key)) this.expandedGroups.delete(key);
    else this.expandedGroups.add(key);
  }

  isGroupExpanded(key: string): boolean {
    return this.expandedGroups.has(key);
  }

  groupGranted(group: ModuleTreeGroup): boolean {
    return group.modules.every(m => this.isGranted(m.id));
  }

  groupPartial(group: ModuleTreeGroup): boolean {
    return this.groupGrantedCount(group) > 0 && !this.groupGranted(group);
  }

  groupGrantedCount(group: ModuleTreeGroup): number {
    return group.modules.filter(m => this.isGranted(m.id)).length;
  }

  toggleGroupAll(group: ModuleTreeGroup): void {
    const target = !this.groupGranted(group);
    group.modules.forEach(m => {
      if (this.isGranted(m.id) !== target) this.onToggleGrant(m.id);
    });
  }

  // ── Scope expansion ──
  toggleScope(moduleId: string): void {
    if (this.expandedModules.has(moduleId)) this.expandedModules.delete(moduleId);
    else this.expandedModules.add(moduleId);
  }

  isScopeExpanded(moduleId: string): boolean {
    return this.expandedModules.has(moduleId);
  }

  // ── Scope summary ──
  scopeSummary(moduleId: string): string {
    const lvl = this.scopeLevel(moduleId);
    if (lvl === 'global') return 'Global';
    if (lvl === 'region') return `Region · ${this.scopeRegions(moduleId).length}`;
    if (lvl === 'country') return `Country · ${this.scopeCountries(moduleId).length}`;
    return `Company · ${this.scopeCompanies(moduleId).length}`;
  }

  scopeLevelLabel(value: DataScopeLevel): string {
    return this.scopeLevels.find(l => l.value === value)?.label ?? 'Global';
  }
}
